/* eslint-disable camelcase */
import BaseComponent from 'scripts/BaseComponent.js';
import Cart from '../../cart/cart'; // cart reference

export default class ProductForm extends BaseComponent {
  constructor(component) {
    super(component);
    this.debug = false;

    this.refs = {
      productJson: '.js-productJson',
      variantIdSelect: '.js-variantId',
      propertyInput: '.js-lineItemProperty',
      productOptionBtn: '.js-productOptionBtn',
      selectedOptionValueText: '.js-selectedOptionValue',
      selectedSellingPlan: '.js-selectedSellingPlan',
      productSubmitBtn: '.js-productSubmitBtn',
      productForm: '.js-productForm',
      price: '.js-price',
      priceCompare: '.js-priceCompare',
      featuredImages: '.js-featuredImage',
      imageZoomBtn: '.js-imageZoomBtn',
      notifyMeForm: '.js-notify-me',
      notifyMeVariantId: '.js-notify-me-variant-id',
      dynamicCheckoutBtns: '#accelerated-checkout',
      productInfoWrap: '.js-product-info-wrap',
      subscriptionContainer: '.js-subscriptionContainer',
    };

    this.dom = {
      $component: component,
      $productJson: document.querySelector(this.refs.productJson),
      $variantIdSelect: this.$get(this.refs.variantIdSelect),
      $propertyInputs: this.$getAll(this.refs.propertyInput),
      $productOptionBtns: this.$getAll(this.refs.productOptionBtn),
      $selectedSellingPlan: this.$get(this.refs.selectedSellingPlan),
      $productSubmitBtn: document.querySelector(this.refs.productSubmitBtn),
      $productForm: this.$get(this.refs.productForm),
      $price: document.querySelector(this.refs.price),
      $priceCompare: document.querySelector(this.refs.priceCompare),
      $featuredImage: document.querySelector(this.refs.featuredImage),
      $notifyMeForm: document.querySelector(this.refs.notifyMeForm),
      $notifyMeVariantId: document.querySelector(this.refs.notifyMeVariantId),
      $dynamicCheckoutBtns: document.querySelector(this.refs.dynamicCheckoutBtns),
      $productInfoWrap: document.querySelector(this.refs.productInfoWrap),
      $subscriptionContainer: this.$get(this.refs.subscriptionContainer),
    };

    this.state = {
      productData: null,
      selectedOptions: {
        option1: null,
        option2: null,
        option3: null,
      },
      activeOptionClass: 'active-option',
      variantNotAvailableClass: 'not-available',
      retailExclusive: false,
      swiperSlideToFirstEvent: 'swiperslidetofirst',
      openAddCardModalEvent: 'openaddcardmodal',
      openCartDrawerEvent: 'opencartdrawer',
      changeHatboxSizeEvent: 'changehatboxsize',
      loadingShimmer: 'LoadingShimmer',
      deliveryDisabledClass: 'delivery-disabled',
      focusZipcodeCheckerEvent: 'focuszipcodechecker',
      variantSelectChangeEvent: 'variantselectchange',
    };

    this.mount();
    this.init();
  }

  mount() {
    const { dom, state } = this;
    super.mount();

    this.setClickWithElement(dom.$productOptionBtns, this.optionSelect);
    this.setOnSubmit(dom.$productForm, this.productFormSubmit);

    this.setOnCustomEvent(state.changeHatboxSizeEvent, this.changeHatboxSize);
  }

  init() {
    this.parseJson();
    this.setSelectedOptions();
  }

  parseJson() {
    const { dom, state } = this;
    state.productData = JSON.parse(dom.$productJson.innerHTML);
    const tags = state.productData.tags.join(',');
    state.retailExclusive = tags.includes('retail-exclusive');
  }

  setSelectedOptions() {
    const { dom, state } = this;
    const defaultId = parseInt(dom.$variantIdSelect.value, 10);
    const variant = state.productData.variants.find(({ id }) => id === defaultId);

    state.selectedOptions.option1 = variant.option1;
    state.selectedOptions.option2 = variant.option2;
    state.selectedOptions.option3 = variant.option3;
  }

  optionSelect(btn) {
    const { state } = this;
    const { optionValue, optionPosition, notAvailable } = btn.dataset;
    const optionKey = `option${optionPosition}`;
    const currentSelectedValue = state.selectedOptions[optionKey];
    if (optionValue === currentSelectedValue) return;

    this.updateSelectedOptionButtons(optionPosition, optionValue, currentSelectedValue);
    this.updateSelectedOptionText(optionPosition, optionValue, !!notAvailable);
    state.selectedOptions[optionKey] = optionValue;
    this.updateSelectedVariant();
  }

  updateSelectedOptionButtons(optionPosition, optionValue, currentSelectedValue) {
    const { state, refs } = this;
    const btnSelector = `${refs.productOptionBtn}[data-option-position="${optionPosition}"]`;
    const currentActiveBtnsSelector = `${btnSelector}[data-option-value="${currentSelectedValue}"]`;
    const setActiveBtnsSelector = `${btnSelector}[data-option-value="${optionValue}"]`;
    const currentActiveBtns = this.$getAll(currentActiveBtnsSelector);
    const setActiveBtns = this.$getAll(setActiveBtnsSelector);
    currentActiveBtns.forEach((btn) => btn.classList.remove(state.activeOptionClass));
    setActiveBtns.forEach((btn) => btn.classList.add(state.activeOptionClass));
  }

  updateSelectedOptionText(optionPosition, optionValue, notAvailable) {
    const { refs } = this;
    const selectedTextSelector = `${refs.selectedOptionValueText}[data-option-position="${optionPosition}"]`;
    const selectedTexts = this.$getAll(selectedTextSelector);
    let updatedText = optionValue;

    if (notAvailable) {
      updatedText = `${optionValue} (no stock)`;
    }

    selectedTexts.forEach((selectedText) => { selectedText.textContent = updatedText; });
  }

  updateSelectedVariant() {
    const { dom, state } = this;

    const keys = Object.keys(state.selectedOptions);
    const selectedVariant = state.productData.variants.find((variant) => (
      keys.every((key) => state.selectedOptions[key] === variant[key])
    ));

    if (selectedVariant.featured_image) {
      this.updateFeaturedImage(selectedVariant.featured_image.src);
      this.updateVariantImageZoomBtn(selectedVariant.featured_image.src);
      this.dispatch(state.swiperSlideToFirstEvent);
    }

    this.updateSubmitBtn(selectedVariant.available);
    dom.$variantIdSelect.value = selectedVariant.id;
    dom.$notifyMeVariantId.value = selectedVariant.id;
    this.updatePrice();

    this.dispatch(state.variantSelectChangeEvent, { id: selectedVariant.id });
    window.triggerSwymVariantEvent(selectedVariant.id);
  }

  updateFeaturedImage(src) {
    const { refs } = this;
    const featuredImages = document.querySelectorAll(refs.featuredImages);
    const sizedSrc = `${src}&width=1024`;

    featuredImages.forEach((img) => {
      img.setAttribute('src', sizedSrc);

      // Update srcset for responsive images
      const srcset = [
        `${src}&width=375 375w`,
        `${src}&width=750 750w`,
        `${src}&width=1024 1024w`,
        `${src}&width=1500 1500w`,
      ].join(', ');
      img.setAttribute('srcset', srcset);
    });
  }

  updateVariantImageZoomBtn(src) {
    const { refs } = this;
    const btns = document.querySelectorAll(`${refs.imageZoomBtn}[data-variant-image]`);

    btns.forEach((btn) => {
      const { imageSrc, imageSrcsets } = btn.dataset;
      const [imageUrl] = imageSrc.split('&');

      const newImageSrc = imageSrc.replace(imageUrl, src);
      btn.setAttribute('data-image-src', newImageSrc);

      const newImageSrcsets = imageSrcsets.replaceAll(imageUrl, src);
      btn.setAttribute('data-image-srcsets', newImageSrcsets);
    });
  }

  updateSubmitBtn(available) {
    const { dom, state } = this;

    if (available) {
      if (!state.retailExclusive) {
        dom.$productSubmitBtn.classList.remove(state.variantNotAvailableClass);
        dom.$productInfoWrap.classList.remove(state.variantNotAvailableClass);
        dom.$productSubmitBtn.removeAttribute('disabled');
        dom.$notifyMeForm.setAttribute('hidden', '');
      }
    } else if (!state.retailExclusive) {
      dom.$productSubmitBtn.setAttribute('disabled', true);
      dom.$productSubmitBtn.classList.add(state.variantNotAvailableClass);
      dom.$productInfoWrap.classList.add(state.variantNotAvailableClass);
      dom.$notifyMeForm.classList.remove('submitted');
      dom.$notifyMeForm.removeAttribute('hidden');
    }
  }

  updatePrice() {
    const { dom } = this;
    const select = dom.$variantIdSelect;
    const { price, compareAt } = select.options[select.selectedIndex].dataset;
    dom.$price.textContent = price;
    if (dom.$priceCompare) {
      dom.$priceCompare.textContent = compareAt;
    }
  }

  getProductProperties() {
    const { dom } = this;
    const properties = {};
    const shopifyProps = document.querySelectorAll("input[name^='properties']");

    dom.$propertyInputs.forEach((input) => {
      if (input.value) properties[input.name] = input.value;
    });

    shopifyProps.forEach((prop) => {
      if (prop.value) {
        const name = prop.name.split(/\[(.*?)\]/)[1];
        properties[name] = prop.value;

        if (name === '_upload') {
          properties.custom_image = true;
        }
      }
    });

    return properties;
  }

  async productFormSubmit() {
    const { dom, state } = this;

    // If zipcode checker component has disabled delivery, focus the checker form.
    if (dom.$productSubmitBtn.classList.contains(state.deliveryDisabledClass)) {
      this.dispatch(state.focusZipcodeCheckerEvent);
      return;
    }

    const id = parseInt(dom.$variantIdSelect.value, 10);
    const properties = this.getProductProperties();
    const sellingPlanId = dom.$selectedSellingPlan.value;

    // if a selling plan is selected, bypass the card modal
    if (state.productData.selling_plan_groups.length > 0 && sellingPlanId) {
      if (sellingPlanId !== '') {
        await Cart.addSubscriptionItem(id, properties, sellingPlanId, 1);
        return;
      }
    }
    this.dispatch(state.openAddCardModalEvent, { id, properties });
  }

  changeHatboxSize({ detail: { products } }) {
    const { state } = this;
    const currentMaterial = state.selectedOptions.option1;
    const roseColor = state.selectedOptions.option2;

    const relatedProduct = products
      .find(({ material }) => currentMaterial === material) || products[0];
    if (!relatedProduct) return;

    let redirectUrl = `/products/${relatedProduct.handle}`;

    const relatedVariant = relatedProduct.variants
      .find(({ rose_color }) => roseColor === rose_color);

    if (relatedVariant) {
      redirectUrl += `?variant=${relatedVariant.id}`;
    }

    window.location.href = redirectUrl;
  }
}
